import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';

import { useStore } from 'src/store';

import type { RigsViewSettingsProvider } from '../drilling-chart/presets/drilling-rigs-chart/rigs-view-settings-provider';
import type { TOption } from '../well-form/types';

export const useGetLegendData = (settingsProvider: RigsViewSettingsProvider, options?: { useShortNames: boolean }) => {
  const { t } = useTranslation();
  const { directories } = useStore();
  const currentColorSetting = settingsProvider.shownAttributesFields['colorTab']?.[0];
  const colorSettings = settingsProvider.colorSettings;

  const nameRefAttr = options?.useShortNames ? 'abbreviatedName' : 'name';
  const defaultNameRefAttr = 'name';

  const data = useMemo(() => {
    if (!currentColorSetting) {
      return [];
    }

    switch (currentColorSetting?.values.fieldId) {
      case 'padReadiness':
      case 'padDocumentationReadiness':
      case 'routeReadiness':
      case 'routeDocumentationReadiness':
      case 'electricityReadiness': {
        return [
          { id: v4(), value: 'var(--green-color)', label: t('labels:ready') },
          { id: v4(), value: 'var(--bright-red)', label: t('labels:notReady') },
          { id: v4(), value: 'var(--orange-color)', label: t('labels:inProcess') },
        ];
      }
      case 'routeType': {
        return [
          { id: v4(), label: t('labels:winter'), value: '#3954D0' },
          { id: v4(), label: t('labels:yearRound'), value: 'green' },
        ];
      }

      case 'cluster': {
        const objects = directories.getObject('Common_Cluster');

        return (
          objects?.reduce((acc: (TOption & { id: string })[], object) => {
            const color = colorSettings?.['Common_Cluster']?.[object.id as number];

            if (!color || color === 'transparent') {
              return acc;
            }

            const label = object.data[nameRefAttr]?.toString() ?? object.data[defaultNameRefAttr]?.toString() ?? '';

            acc.push({
              id: v4(),
              label,
              value: color,
            });

            return acc;
          }, []) ?? []
        );
      }

      default: {
        const objectType = currentColorSetting.view.refObjectType;

        if (!objectType) {
          return [];
        }

        const objects = directories.getObject(objectType);

        if (!objects) {
          return [];
        }

        return [...objects]
          .sort((a, b) => {
            const aLabel = a.data[nameRefAttr]?.toString() ?? a.data[defaultNameRefAttr]?.toString() ?? '';
            const bLabel = b.data[nameRefAttr]?.toString() ?? b.data[defaultNameRefAttr]?.toString() ?? '';

            return aLabel.localeCompare(bLabel);
          })
          .reduce((acc: (TOption & { id: string })[], object) => {
            const color = colorSettings?.[objectType]?.[object.id as number];

            if (!color || color === 'transparent') {
              return acc;
            }

            const label = object.data[nameRefAttr]?.toString() ?? object.data[defaultNameRefAttr]?.toString() ?? '';

            acc.push({
              id: v4(),
              label,
              value: color,
            });

            return acc;
          }, []);
      }
    }
  }, [colorSettings, currentColorSetting, directories, nameRefAttr, t]);

  const title = useMemo(() => {
    if (!currentColorSetting?.values.fieldId) {
      return 'Unknown title';
    }

    return t(`labels:${currentColorSetting.values.fieldId}`);
  }, [currentColorSetting, t]);

  return {
    title,
    data,
  };
};
